import {
  BottomSheet,
  CbhIcon,
  Illustration,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor/platformUtils";
import { share } from "@src/appV2/NativeAdapters/share.adapter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { USER_EVENTS } from "@src/constants";

import { IconButton } from "../../../components/IconButton";
import ErrorComponent from "../../components/ErrorComponent";
import { useGetInviteFriendLink } from "../../hooks/useGetInviteFriendLink";

interface Props {
  modalState: UseModalState;
  onClose: () => void;
}

export default function WorkWithFriendsInviteBottomSheet(props: Props) {
  const { modalState, onClose } = props;

  const { showSuccessToast } = useToast();

  const { data: inviteFriendLinkData, isLoading, isRefetching, isError } = useGetInviteFriendLink();

  return (
    <BottomSheet modalState={modalState}>
      <Stack flex={1} flexDirection="column">
        <Stack flex={1} alignItems="flex-end" p={4}>
          <IconButton
            aria-label="close"
            variant="outlined"
            iconType="close"
            size="small"
            onClick={onClose}
          />
        </Stack>
        <Stack px={6} pb={10} alignItems="center">
          <Stack mt={10} mx={10} justifyContent="center" alignItems="center">
            {isError ? (
              <ErrorComponent />
            ) : (
              <>
                <Illustration type="mail-person" />
                <Title
                  component="h2"
                  variant="h1"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                  })}
                  py={4}
                  align="center"
                >
                  Add a Friend
                </Title>
                <Title
                  component="h6"
                  variant="h6"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                  align="center"
                >
                  Connect with a friend so you can both see the shifts you book. Share this link
                  with a friend so they can send you a request to connect.
                </Title>
              </>
            )}
          </Stack>
          <Stack spacing={4} flex={1} width="100%" mt={10}>
            <LoadingButton
              isLoading={isLoading || isRefetching}
              variant="contained"
              disabled={!isDefined(inviteFriendLinkData?.data.attributes.url) || isError}
              onClick={async () => {
                if (!isDefined(inviteFriendLinkData?.data.attributes.url)) {
                  logError(USER_EVENTS.COPY_INVITE_FRIEND_LINK_FAILURE, {
                    error: "Invite friend link is not defined",
                    metadata: {
                      inviteFriendLinkData,
                    },
                  });
                  return;
                }

                if (isCapacitorPlatform()) {
                  await share({
                    title: "Add Friends on Clipboard",
                    text: `Hey! Want to add me as a friend on Clipboard so we can see the shifts the other has booked and work together? Go here: ${inviteFriendLinkData.data.attributes.url}`,
                  });
                } else {
                  await navigator.clipboard.writeText(inviteFriendLinkData.data.attributes.url);
                  showSuccessToast("Link copied to clipboard");
                }
              }}
            >
              <CbhIcon sx={{ mr: 2 }} type={isCapacitorPlatform() ? "arrow-up" : "copy"} />{" "}
              {isCapacitorPlatform() ? "Share Profile Link" : "Copy Profile Link"}
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
